export const typescriptPosts = [
  {
    id: 11,
    slug: "advanced-typescript-patterns",
    title: "Advanced TypeScript Patterns",
    excerpt: "Master conditional types, template literal types, and mapped types to build rock-solid libraries.",
    date: "May 01, 2024",
    readTime: "13 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Type Powerhouse</h2>
      <p>TypeScript is more than just documentation; it's a powerful logic engine. By mastering advanced types, you can create APIs that are both incredibly flexible and completely type-safe.</p>
      <h3>Conditional Types</h3>
      <p>Learn how to use <code>T extends U ? X : Y</code> to create dynamic types that adapt to their inputs. This is the secret behind complex utility libraries and framework internals that provide perfect autocompletion.</p>
      <h3>Template Literal Types</h3>
      <p>Discover how to manipulate strings at the type level. Whether you're building a typed CSS library or a router with path parameters, template literal types allow you to catch formatting errors before you even save the file.</p>
      <h3>Recursive Types</h3>
      <p>Master the art of representing nested structures like trees or deeply nested configuration objects. We'll show you how to write types that can traverse any depth without losing type safety.</p>
    `,
    keywords: "TypeScript, Advanced Types, Type Programming"
  },
  {
    id: 12,
    slug: "typescript-for-enterprise-apps",
    title: "TS for Enterprise Apps",
    excerpt: "Best practices for architecting large-scale TypeScript projects with focus on modularity and type sharing.",
    date: "May 05, 2024",
    readTime: "11 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1516116216624-53e697fedbea?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Foundations for Scale</h2>
      <p>Building a project with 100+ developers requires a different approach than a small side project. TypeScript provides the tools to manage this complexity, but only if you use them correctly.</p>
      <h3>Project References</h3>
      <p>Learn how to split your massive codebase into smaller, independent projects while maintaining full type safety across boundaries. This significantly improves build times and IDE performance in large monorepos.</p>
      <h3>Global Type Management</h3>
      <p>Discover strategies for sharing types between frontend and backend. We'll explore automated type generation from your database or API schemas, ensuring your entire stack is in perfect sync.</p>
      <h3>Strict Mode Strategy</h3>
      <p>We'll discuss the path to "strict: true" for existing projects. It's not always an all-or-nothing move. Learn how to use granular strictness flags to gradually improve your codebase quality without stopping development.</p>
    `,
    keywords: "Enterprise TypeScript, Scalability, Project References"
  },
  {
    id: 13,
    slug: "typescript-utility-types-mastery",
    title: "Utility Types Mastery",
    excerpt: "Stop duplicating code. Learn how to derive perfect types from your existing models using built-in utilities.",
    date: "May 10, 2024",
    readTime: "9 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Single Source of Truth</h2>
      <p>Don't create five different versions of your User interface. Derive them! TypeScript's utility types are the key to a DRY (Don't Repeat Yourself) type system.</p>
      <h3>Pick, Omit, and Partial</h3>
      <p>Master the basics for creating views of your data. We'll show you how to create the perfect "create" vs "update" vs "preview" types from a single base model, ensuring that updates to the base propagate everywhere.</p>
      <h3>Required and Readonly</h3>
      <p>Protect your data from accidental modification and ensure that critical fields are always present. We'll discuss the design philosophy of immutability and how TypeScript enforces it at the type level.</p>
      <h3>ReturnTypes and Parameters</h3>
      <p>Learn how to steal types from functions. These utilities are life-savers when working with third-party libraries that don't export their internal types, allowing you to maintain full safety even in opaque environments.</p>
    `,
    keywords: "TypeScript Utilities, Clean Code, DRY Types"
  },
  {
    id: 14,
    slug: "typescript-performance-tuning",
    title: "TypeScript Performance Tuning",
    excerpt: "Is your IDE slow? Learn how to identify and fix expensive types that are dragging down your development speed.",
    date: "May 15, 2024",
    readTime: "10 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Fighting the Red Squigglies</h2>
      <p>As your type system grows more sophisticated, it can become a burden on your computer. A slow type checker is a productivity killer. Learn how to keep your types fast and efficient.</p>
      <h3>Identifying Expensive Types</h3>
      <p>We'll look at the "trace-resolution" flag and how to use it to find the specific types that are causing bottlenecks. Deeply nested recursive types and massive union types are often the culprits.</p>
      <h3>Optimization Strategies</h3>
      <p>Discover how to simplify complex types without losing safety. We'll explore the use of <code>interface</code> over <code>type</code> for better caching and how to structure your utility types to minimize re-computations by the compiler.</p>
      <h3>The Impact of Large Libraries</h3>
      <p>Understand how third-party types (like those from large UI kits) impact your performance and how to use "skipLibCheck" and other flags to keep your local development experience snappy.</p>
    `,
    keywords: "TypeScript Performance, Optimization, Compiler Speed"
  },
  {
    id: 15,
    slug: "integrating-typescript-with-nextjs",
    title: "TS with Next.js 14",
    excerpt: "Perfectly typed Server Components, Actions, and API routes in the latest Next.js version.",
    date: "May 20, 2024",
    readTime: "12 min read",
    category: "TypeScript",
    image: "https://images.unsplash.com/photo-1618477247222-acbdb0e159b3?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Ultimate Combination</h2>
      <p>Next.js and TypeScript are a match made in heaven. With the latest features in Next.js 14, the integration is tighter than ever, offering end-to-end type safety across the network boundary.</p>
      <h3>Typed Server Actions</h3>
      <p>Learn how to define Server Actions that are fully typed from the form component down to the server-side logic. We'll show you how to use <code>zod</code> and <code>ts-rest</code> to ensure that your form data always matches your expectations.</p>
      <h3>Typed Routing and Metadata</h3>
      <p>Discover Next.js's new experimental typed routing feature. Catch broken links at compile-time! We'll also cover how to write perfectly typed dynamic metadata for SEO that adapts to your page data.</p>
      <h3>Component Props and Context</h3>
      <p>Master the patterns for typing complex Next.js layouts and components. We'll discuss the nuances of <code>AppRouter</code> vs <code>PagesRouter</code> typing and how to shared typed state in a server-centric world.</p>
    `,
    keywords: "Next.js, TypeScript, Full Stack Safety, Web Development"
  }
];
